import React, { useState, useEffect } from 'react';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory } from '../utils/i18n';
import { 
  Building2, Hammer, HardHat, Download, Copy, Check, 
  ArrowLeft, Sparkles, FileText, Upload, RefreshCw, FileCheck, X,
  Globe, ShieldCheck, CheckSquare, AlertTriangle
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import ExcelExportButton from './ExcelExportButton';
import { DigitalSignatureModal } from './DigitalSignatureModal';
import { getFieldLabel, getFieldValue, getNotesText } from '../utils/demoPresetsI18n';

interface ConstructionAgentViewProps {
  agentId: string;
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const DEFAULT_CONSTRUCTION_DEMO: Record<string, {
  field1LabelUk: string; field1Value: string;
  field2LabelUk: string; field2Value: string;
  field3LabelUk: string; field3Value: string;
  field4LabelUk: string; field4Value: string;
  field5LabelUk: string; field5Value: string;
  field6LabelUk: string; field6Value: string;
  notesText: string;
}> = {
  'construction-hvac-energy-passport': {
    field1LabelUk: "Тип будівлі & Функціональне призначення",
    field1Value: "Офісно-діловий центр з підземним паркінгом (Клас енергоефективності цільовий: Клас А / WT 2021)",
    field2LabelUk: "Нормативна база (ДБН В.2.6-31 / WT 2021 / EPBD)",
    field2Value: "ДБН В.2.6-31:2021 («Теплова ізоляція будівель») та Warunki Techniczne WT 2021 (Польща/ЄС)",
    field3LabelUk: "Геометричні параметри & Площа огороджень (м², м³)",
    field3Value: "Загальна площа: 4 850 м², Опалювальний об’єм: 15 200 м³, Площа зовнішніх стін: 2 400 м², Вікна: 850 м²",
    field4LabelUk: "Матеріали огороджувальних конструкцій & Утеплення",
    field4Value: "Стіни: Керамоблок 250 мм + Базальтова мінвата 150 мм (R = 4.2 м²·К/Вт) / Вікна: 3-камерні склопакети (U = 0.85 Вт/м²·К)",
    field5LabelUk: "Джерело теплопостачання & Системи вентиляції (ОВК)",
    field5Value: "Каскад геотермальних теплових насосів (COP 4.2, 180 кВт) + Припливно-витяжна вентиляція з рекуперацією тепла (ККД 82%)",
    field6LabelUk: "Річне питоме енергоспоживання (EP / kWh/m²·рік)",
    field6Value: "Питоме споживання первинної енергії EP = 58.4 кВт·год/м²·рік (відповідає жорстким нормам nZEB майже нульового споживання)",
    notesText: "Виконати повний теплотехнічний розрахунок огороджувальних конструкцій об'єкта, обчислити тепловтрати через трансмісію та вентиляцію, розрахувати потужність системи опалення й кондиціонування та сформувати офіційний Енергетичний сертифікат (Świadectwo charakterystyki energetycznej) у Word (.docx) за ДСТУ 4163:2020 та Excel (.xlsx з формулами)."
  },
  'construction-acts-auditor-kb2-kb3': {
    field1LabelUk: "Тип акта / Документа & Об'єкт будівництва",
    field1Value: "Акт приймання виконаних будівельних робіт (форма № КБ-2в) та Довідка КБ-3 / Капітальний ремонт лікарні",
    field2LabelUk: "Договірна ціна & Формат розрахунку",
    field2Value: "Тверда договірна ціна (14 850 000 грн з ПДВ) / Період: липень 2026 р.",
    field3LabelUk: "Обсяги робіт за актом проти Проєкту (м³, т, м²)",
    field3Value: "Укладання бетону B25: 420 м³ (за актом) проти 310 м³ (за проектом та журналом робіт); Монтаж металоконструкцій: 24.5 т (проти 18.2 т)",
    field4LabelUk: "Вартість матеріалів & Експлуатації машин",
    field4Value: "Бетон B25: 3 850 грн/м³ (ринкова 2 950 грн/м³ — завищення на 30%); Робота автокрана 25т: 120 маш-год (завищено на 40 маш-год)",
    field5LabelUk: "Загальновиробничі витрати (ЗВВ) & Прибуток",
    field5Value: "ЗВВ розраховані за усередненими показниками ДБН (коефіцієнт 1.25), прибуток 4.8 грн/люд-год, єдиний внесок 22%",
    field6LabelUk: "Виконавча документація & Журнали робіт",
    field6Value: "Акти на приховані роботи № 12–28, паспорти якості на арматуру А500С, протоколи випробування бетону на 28 добу, журнал авторського нагляду",
    notesText: "Провести детальний комплаєнс-аудит наданого Акта приймання виконаних будівельних робіт форми № КБ-2в та Довідки про вартість виконаних робіт форми № КБ-3. Перевірити відповідність обсягів робіт робочому проєкту та журналу робіт (форма КБ-6). Зіставити ціни на будівельні матеріали з даними моніторингу Мінрегіону/ДерждорНДІ. Перевірити правильність нарахування загальновиробничих витрат, кошторисного прибутку та ПДВ. Сформувати дефектну відомість завищень та підсумковий Акт зауважень замовника у Word (.docx) за ДСТУ 4163:2020."
  },
  'construction-estimate-parser-avk': {
    field1LabelUk: "Формат кошторисного файлу & Програма",
    field1Value: "Файл проекту АВК-5 (.bdk) / Будівельний комплекс 'Нова Школа' (Локальні кошториси № 02-01-01)",
    field2LabelUk: "Бази ціноутворення (РЕКН / КНУ / SEKOCENBUD)",
    field2Value: "Кошторисні норми України (КНУ 2021) / Бюлетень цін ДерждорНДІ 2026",
    field3LabelUk: "Загальна кошторисна вартість & Глави 1-12",
    field3Value: "42 300 000 грн (Глава 2: Основні об'єкти будівництва — 31.2 млн грн, Устаткування — 6.5 млн грн)",
    field4LabelUk: "Тарифна сітка робітників & Розряд робіт",
    field4Value: "Середній розряд 3.8 / Тарифна ставка 185.40 грн/люд-год (згідно наказу Мінрегіону)",
    field5LabelUk: "Кошторисні норми експлуатації машин (СЕКМ)",
    field5Value: "Екскаватор Komatsu 1.2 м³ — 1 850 грн/маш-год, Автобетононасос 36м — 3 400 грн/маш-год",
    field6LabelUk: "Нормативний рівень кошторисного прибутку & ЗВВ",
    field6Value: "Прибуток 5.4 грн/люд-год, Адміністративні витрати 2.15 грн/люд-год, ЗВВ за розрахунком № 1",
    notesText: "Здійснити комплексний аналіз та парсинг завантаженого кошторисного файлу АВК-5 / Norma PRO. Перевірити обґрунтованість застосування індивідуальних розцінок, порівняти ціни на основні ціноутворюючі будівельні ресурси (бетон, арматура, утеплювач мінераловатний, кабелі) з середньоринковими цінами регіону. Виявити невиправдані націнки у розрахунках № 1-3 загальновиробничих витрат та сформувати аудиторський звіт оптимізації кошторису у Word (.docx)."
  },
  'construction-pto-work-log-auditor': {
    field1LabelUk: "Назва об'єкта & Номер Загального журналу робіт",
    field1Value: "Капітальний ремонт мостового переходу через р. Дніпро / Загальний журнал робіт № 01/2026 (Форма КБ-6)",
    field2LabelUk: "Генеральний підрядник & Інженер технагляду",
    field2Value: "ТОВ 'Мостобуд Інжиніринг' / Сертифікат технагляду АТ № 003912",
    field3LabelUk: "Складені акти на закриття прихованих робіт",
    field3Value: "Акти № 1-45: Армування ростверків опор № 1-4, гідроізоляція монолітної плити, влаштування деформаційних швів",
    field4LabelUk: "Спеціальні журнали (Бетонних/Зварювальних робіт)",
    field4Value: "Журнал догляду за бетоном (температурні карти зима 2026), Журнал зварювальних робіт та антикорозійного захисту",
    field5LabelUk: "Лабораторні випробування & Сертифікати якості",
    field5Value: "Протоколи руйнівного контролю бетону B35 на 7 та 28 добу (акредитована лабораторія), сертифікати УкрСЕПРО на сталь 15ХСНД",
    field6LabelUk: "Геодезичні виконавчі зйомки & Відхилення",
    field6Value: "Виконавча схема планово-висотного положення опор (відхилення осі: +4 мм при допуску ±10 мм за ДБН В.2.3-22:2009)",
    notesText: "Провести повну перевірку ведення виконавчої виробничої документації об'єкта. Перевірити хронологічну відповідність записів у Загальному журналі робіт (КБ-6) датам складання актів на приховані роботи та датам лабораторних випробувань міцності бетону. Перевірити наявність обов'язкових підписів авторського та технічного нагляду, сертифікатів відповідності на всі використані матеріали та скласти Підсумковий реєстр виконавчої документації для здачі в ДІАМ/PINB."
  },
  'construction-bim-ifc-boq-extractor': {
    field1LabelUk: "Файл 3D BIM моделі (IFC / Revit / Navisworks)",
    field1Value: "Модель житлового будинку 'Block_A_Structural.ifc' (Схема IFC4 / LoD 350)",
    field2LabelUk: "Програмне середовище моделювання & Координація",
    field2Value: "Autodesk Revit 2026 / Об'єднана координатна система WGS-84 / УСК-2000",
    field3LabelUk: "Конструктивні об'єми за 3D геометрією (Takeoff)",
    field3Value: "Монолітний залізобетон каркасу: 3 840 м³, Арматура сталева A500C: 345.6 т, Зовнішні стіни газоблок D400: 1 210 м³",
    field4LabelUk: "Відомість обсягів робіт підрядника (Тендерний BOQ)",
    field4Value: "Підрядник задекларував у кошторисі: Бетон — 4 250 м³ (+10.6% завищення), Арматура — 398.0 т (+15.1% завищення)",
    field5LabelUk: "Інженерні системи MEP & Трубопроводи",
    field5Value: "Труби поліпропіленові опалення d32-d110: 4 200 м.п., Вентиляційні повітроводи оцинковані: 2 150 м²",
    field6LabelUk: "Перевірка просторових колізій (Clash Detection)",
    field6Value: "Виявлено 14 жорстких перетинів повітроводів вентиляції з несучими балками перекриття на відмітці +12.000",
    notesText: "Здійснити автоматичний експорт та верифікацію фізичних об'ємів матеріалів з параметричної 3D BIM моделі (формат IFC). Зіставити точні геометричні об'єми бетонування, армування та кладки з тендерним кошторисом підрядника. Виявити всі приховані розбіжності та геометричні завищення. Згенерувати Звіт аудиту BIM-обмірів та просторових колізій у Word (.docx)."
  },
  'construction-diam-e-system-auditor': {
    field1LabelUk: 'Назва об\'єкта будівництва & Клас наслідків', field1Value: 'Нове будівництво житлового комплексу / СС2 (Середні наслідки)',
    field2LabelUk: 'Статус реєстрації в ЄДЕССБ (Реєстр будівельної діяльності)', field2Value: 'МБУО отримано, проєкт подано на експертизу в ЄДЕССБ',
    field3LabelUk: 'Цільова дія в ДІАМ', field3Value: 'Отримання дозволу на виконання будівельних робіт',
    field4LabelUk: 'Кваліфікаційні сертифікати ГАП / ГІП', field4Value: 'Сертифікат ГАП Серія АА №004812 (дійсний)',
    field5LabelUk: 'Застосовні нормативні стандарти ДБН', field5Value: 'ДБН В.1.2-14:2018, ДБН А.2.2-3:2014, ДБН В.2.2-15:2019',
    field6LabelUk: 'Кадастровий номер земельної ділянки', field6Value: '8000000000:79:045:0012 (Цільове призначення: 02.03)',
    notesText: 'Перевірити повний комплект проєктної документації перед подачею до ДІАМ через ЄДЕССБ, перевірити відповідність містобудівним умовам та обмеженням (МУО), наявність розрахунку класу наслідків СС2 та сформувати чек-лист комплаєнсу.'
  },
  'construction-war-damage-evaluator': {
    field1LabelUk: 'Тип та адреса пошкодженого майна', field1Value: 'Багатоквартирний житловий будинок / м. Ірпінь, вул. Соборна',
    field2LabelUk: 'Категорія пошкоджень об\'єкта (ПКМУ № 423)', field2Value: 'II категорія пошкоджень (потребує капітального ремонту)',
    field3LabelUk: 'Пошкоджені конструктивні елементи', field3Value: 'Покрівля 450 м², плити перекриття 4 поверху, вікна 120 шт',
    field4LabelUk: 'Оціночна вартість відбудови / відновлення', field4Value: '8 450 000 грн (згідно укрупнених показників кошторисної вартості)',
    field5LabelUk: 'Статус комісійного обстеження', field5Value: 'Акт комісійного обстеження складено / Потрібен дефектний акт',
    field6LabelUk: 'Подача до Реєстру Пошкодженого та Знищеного Майна', field6Value: 'Подано через Портал Дія (Заявка № 481920)',
    notesText: 'Скласти дефектний акт та локальний кошторис на капітальний ремонт пошкодженого багатоквартирного будинку за ПКМУ № 423, категоризувати ступінь руйнувань та підготувати підсумковий звіт для компенсаційного фонду.'
  },
  'construction-dpt-urban-planner': {
    field1LabelUk: 'Площа території ДПТ (га) & Локація', field1Value: '28.5 га / Київська область, Бучанський район',
    field2LabelUk: 'Поточне та проектне цільове призначення', field2Value: 'Землі промисловості → Житлова та громадська забудова',
    field3LabelUk: 'Запланована чисельність населення ДПТ', field3Value: '4 200 мешканців (Розрахункова щільність 150 осіб/га)',
    field4LabelUk: 'Застосовний ДБН містобудування', field4Value: 'ДБН Б.2.2-12:2019 «Планування та забудова територій»',
    field5LabelUk: 'Забезпеченість соцінфраструктурою', field5Value: 'Дитячий садок на 140 місць + Початкова школа на 360 місць',
    field6LabelUk: 'Червоні лінії & Охоронні зони', field6Value: 'Відступ від ЛЕП 110 кВ — 25 м, відступ від автодороги — 50 м',
    notesText: 'Скласти містобудівне обґрунтування та концепцію Детального Плану Території (ДПТ) площею 28.5 га відповідно до ДБН Б.2.2-12:2019, розрахувати ємність паркувальних місць, баланс території та підготувати матеріали для громадських слухань.'
  },
  'construction-electrical-engineer': {
    field1LabelUk: 'Тип об\'єкта & Категорія надійності електропостачання',
    field1ValueUk: 'Торгово-розважальний центр з підземним паркінгом / II категорія надійності (2 незалежні вводи 10 кВ)',
    field1ValuePl: 'Centrum handlowo-rozrywkowe z parkingiem podziemnym / Kategoria II niezawodności zasilania',
    field1ValueEn: 'Shopping & Entertainment Center with Underground Parking / Category II Reliability (Dual 10kV Feeds)',
    field1Value: 'Торгово-розважальний центр з підземним паркінгом / II категорія надійності (2 незалежні вводи 10 кВ)',

    field2LabelUk: 'Встановлена & Розрахункова потужність (Pвст / Pроз, кВт)',
    field2ValueUk: 'Pвст = 850 кВт, Pроз = 580 кВт (cos φ = 0.92, коефіцієнт попиту Кп = 0.68)',
    field2ValuePl: 'Pust = 850 kW, Pwzgl = 580 kW (cos φ = 0.92, współczynnik zapotrzebowania Kz = 0.68)',
    field2ValueEn: 'Installed P = 850 kW, Demand P = 580 kW (cos φ = 0.92, demand factor Kd = 0.68)',
    field2Value: 'Pвст = 850 кВт, Pроз = 580 кВт (cos φ = 0.92, коефіцієнт попиту Кп = 0.68)',

    field3LabelUk: 'Трансформаторна підстанція & Резервне живлення',
    field3ValueUk: 'КТП 2х630 кВА (10/0.4 кВ, сухі трансформатори) + ДГУ 250 кВА (автозапуск АВР для систем пожежогасіння)',
    field3ValuePl: 'Stacja transformatorowa 2x630 kVA (10/0.4 kV) + Agregat prądotwórczy 250 kVA z SZR',
    field3ValueEn: 'Substation 2x630 kVA (10/0.4 kV Dry Type) + 250 kVA Diesel Generator with ATS for Fire Systems',
    field3Value: 'КТП 2х630 кВА (10/0.4 кВ, сухі трансформатори) + ДГУ 250 кВА (автозапуск АВР для систем пожежогасіння)',

    field4LabelUk: 'Кабельні лінії & Перерізи (ВВГнг-LS / N2XH)',
    field4ValueUk: 'Магістральні лінії: N2XH-FE180/E90 4x185 мм², розподільчі лінії: ВВГнг-LS 5x10 мм², 3x2.5 мм²',
    field4ValuePl: 'Linie magistralne: N2XH-FE180/E90 4x185 mm², linie rozdzielcze: YDYżo 5x10 mm², 3x2.5 mm²',
    field4ValueEn: 'Main distribution: N2XH-FE180/E90 4x185 mm², sub-circuits: Flame-retardant LSZH 5x10 mm², 3x2.5 mm²',
    field4Value: 'Магістральні лінії: N2XH-FE180/E90 4x185 мм², розподільчі лінії: ВВГнг-LS 5x10 мм², 3x2.5 мм²',

    field5LabelUk: 'Захисна автоматика & Селективність (ПЗВ / АВ / ПЗІП)',
    field5ValueUk: 'Ввідні автомати Emax 1000A з мікропроцесорним розчіплювачем, диференційні автомати 30мА, ПЗІП Класу 1+2',
    field5ValuePl: 'Wyłączniki główne 1000A z wyzwalaczem mikroprocesorowym, RCD 30mA, ograniczniki przepięć Typ 1+2',
    field5ValueEn: 'Main ACB 1000A with Microprocessor Trip Unit, 30mA RCDs, Type 1+2 Surge Protection Devices',
    field5Value: 'Ввідні автомати Emax 1000A з мікропроцесорним розчіплювачем, диференційні автомати 30мА, ПЗІП Класу 1+2',

    field6LabelUk: 'Нормативна база (ПУЕ:2017 / ДБН В.2.5-23:2010 / PN-HD 60364)',
    field6ValueUk: 'ПУЕ:2017 («Правила улаштування електроустановок»), ДБН В.2.5-23:2010 («Проектування електрообладнання»)',
    field6ValuePl: 'PN-HD 60364 (Instalacje elektryczne niskiego napięcia), Rozporządzenie WT 2021',
    field6ValueEn: 'PUE:2017, DBN V.2.5-23:2010 (Building Electrical Design), IEC 60364 Standards',
    field6Value: 'ПУЕ:2017 («Правила улаштування електроустановок»), ДБН В.2.5-23:2010 («Проектування електрообладнання»)',

    notesTextUk: 'Виконати повний інженерний розрахунок системи електропостачання об\'єкта (ТРЦ): розрахувати струмові навантаження за ДБН В.2.5-23:2010, спад напруги в кабельних лініях (ΔU ≤ 4%), струми однофазного та трифазного КЗ, перевірити селективність автоматичних вимикачів, розрахувати контур захисного заземлення (Rз ≤ 4 Ом) та блискавкозахист за ДСТУ EN 62305. Сформувати підсумковий технічний звіт та однолінійну схему ВРУ у Word (.docx) за ДСТУ 4163:2020.',
    notesTextPl: 'Przeprowadzić pełne obliczenia inżynieryjne instalacji elektrycznej obiektu: wyznaczyć prądy obliczeniowe według normy PN-HD 60364, spadki napięć na trasach kablowych (ΔU ≤ 4%), prądy zwarciowe, selektywność zabezpieczeń oraz uziom fundamentowy (R ≤ 4 Ohm). Wygenerować raport techniczny ze schematem jednokreskowym w formacie Word (.docx).',
    notesTextEn: 'Execute complete electrical engineering calculations for the commercial complex: compute load currents per DBN V.2.5-23 / IEC 60364, voltage drop across cable runs (ΔU ≤ 4%), 1-phase and 3-phase short-circuit currents, protection selectivity, and grounding resistance (R ≤ 4 Ohm). Produce comprehensive engineering report with single-line diagram in Word (.docx).',
    notesText: 'Виконати повний інженерний розрахунок системи електропостачання об\'єкта (ТРЦ): розрахувати струмові навантаження за ДБН В.2.5-23:2010, спад напруги в кабельних лініях (ΔU ≤ 4%), струми однофазного та трифазного КЗ, перевірити селективність автоматичних вимикачів, розрахувати контур захисного заземлення (Rз ≤ 4 Ом) та блискавкозахист за ДСТУ EN 62305. Сформувати підсумковий технічний звіт та однолінійну схему ВРУ у Word (.docx) за ДСТУ 4163:2020.'
  }
};

const LOADING_STEPS_UK = [
  "Ініціалізація будівельного експертного аудитора...",
  "Звірка обсягів робіт КБ-2в із проєктними специфікаціями та відомостями обмірів...",
  "Ціновий моніторинг вартості бетону, металу, спецтехніки (ДерждорНДІ / SEKOCENBUD)...",
  "Перевірка формул ЗВВ, кошторисного прибутку та єдиного внеску за ДСТУ / KNR...",
  "Формування підсумкового Акта розбіжностей та аудиторського висновку у Word (.docx)..."
];

const LOADING_STEPS_PL = [
  "Inicjalizacja audytora protokołów i obmiarów budowlanych...",
  "Weryfikacja obmiarów robót z projektem wykonawczym i wpisami w Dzienniku Budowy...",
  "Analiza cen materiałów i stawek sprzętu według baz SEKOCENBUD i KNR...",
  "Audyt narzutów kosztów pośrednich Kp, zysku Z oraz podatku VAT KSeF...",
  "Generowanie końcowego protokołu rozbieżności i raportu technicznego Word (.docx)..."
];

const LOADING_STEPS_EN = [
  "Initializing construction acceptance acts and payment certificates auditor...",
  "Reconciling bill of quantities (BOQ) against structural drawings and site logs...",
  "Benchmarking material and equipment hire rates against regional price indices...",
  "Verifying overhead markup formulas, contractor profits, and tax equations...",
  "Generating final discrepancy statement and technical audit report in Word (.docx)..."
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Building2 className={className} />;
  return <IconComponent className={className} />;
};

export default function ConstructionAgentView({
  agentId,
  language,
  onBack, 
  onGoHome,
  useEnterpriseContext,
  enterpriseName
}: ConstructionAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const agentConfig = agentsConfig.find((a: any) => a.id === agentId) || {
    nameUk: 'Будівельний Аналітичний Модуль',
    nameEn: 'Construction AI Agent',
    namePl: 'Moduł Analityki Budowlanej',
    descriptionUk: 'Аудит будівництва, актів КБ-2в/КБ-3, ДІАМ та кошторисів.',
    descriptionEn: 'Construction, KB-2v/KB-3 acts, DIAM and cost estimate audit.',
    descriptionPl: 'Audyt budowlany, protokołów odbioru robót, obmiarów i norm KNR.',
    icon: 'Building2',
    categoryUk: 'БУДІВНИЦТВО & ДБН',
    categoryEn: 'CONSTRUCTION & DBN',
    categoryPl: 'BUDOWNICTWO & FIDIC'
  };

  const defaultDemo = DEFAULT_CONSTRUCTION_DEMO[agentId] || DEFAULT_CONSTRUCTION_DEMO['construction-acts-auditor-kb2-kb3'] || DEFAULT_CONSTRUCTION_DEMO['construction-diam-e-system-auditor'];

  const [field1, setField1] = useState(getFieldValue(defaultDemo, 1, language));
  const [field2, setField2] = useState(getFieldValue(defaultDemo, 2, language));
  const [field3, setField3] = useState(getFieldValue(defaultDemo, 3, language));
  const [field4, setField4] = useState(getFieldValue(defaultDemo, 4, language));
  const [field5, setField5] = useState(getFieldValue(defaultDemo, 5, language));
  const [field6, setField6] = useState(getFieldValue(defaultDemo, 6, language));
  const [notesText, setNotesText] = useState(getNotesText(defaultDemo, language));

  const [attachedFiles, setAttachedFiles] = useState<Array<{ name: string; size: number; text: string }>>([]);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [reportResult, setReportResult] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);

  useEffect(() => {
    setField1(getFieldValue(defaultDemo, 1, language));
    setField2(getFieldValue(defaultDemo, 2, language));
    setField3(getFieldValue(defaultDemo, 3, language));
    setField4(getFieldValue(defaultDemo, 4, language));
    setField5(getFieldValue(defaultDemo, 5, language));
    setField6(getFieldValue(defaultDemo, 6, language));
    setNotesText(getNotesText(defaultDemo, language));
    setReportResult(null);
  }, [defaultDemo, language]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isProcessing) {
      setLoadingStepIndex(0);
      const steps = isPl ? LOADING_STEPS_PL : isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex(prev => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing, isUk, isPl]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsReadingFile(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/extract-text', { method: 'POST', body: formData });

        if (res.ok) {
          const data = await res.json();
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: data.text || '' }]);
        } else {
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: `[Файл ${file.name} додано]` }]);
        }
      }
    } catch (err) {
      console.error('File upload error:', err);
    } finally {
      setIsReadingFile(false);
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRunAnalysis = async () => {
    setIsProcessing(true);
    setReportResult(null);
    setErrorMessage(null);
    setLoadingStepIndex(0);

    try {
      const combinedDocText = attachedFiles.map(f => `--- ФАЙЛ: ${f.name} ---\n${f.text}`).join('\n\n');
      
      const inputPrompt = `
[ЗАВДАННЯ / ДОКУМЕНТ ДЛЯ АУДИТУ]:
${notesText}

[ВХІДНІ ДАНІ]:
- ${defaultDemo.field1LabelUk}: ${field1}
- ${defaultDemo.field2LabelUk}: ${field2}
- ${defaultDemo.field3LabelUk}: ${field3}
- ${defaultDemo.field4LabelUk}: ${field4}
- ${defaultDemo.field5LabelUk}: ${field5}
- ${defaultDemo.field6LabelUk}: ${field6}

${combinedDocText ? `[ДОДАНІ ДОКУМЕНТИ / КОШТОРИСИ / АКТИ]:\n${combinedDocText}` : ''}
`;

      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: agentConfig.nameUk || "Аудитор Будівельних Актів КБ-2в, КБ-3 & Кошторисів (ДСТУ / KNR / FIDIC)",
          agentId: agentId,
          inputs: {
            task_description: inputPrompt,
            agent_name: agentConfig.nameUk,
            target_language: isPl ? 'pl' : isUk ? 'uk' : 'en'
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isPl ? 'Błąd wykonania audytu budowlanego.' : isUk ? 'Помилка виконання будівельного аналізу.' : 'Execution failed.'));
      }

      const data = await response.json();

      if (data.jobId) {
        const pollJob = async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (!statusRes.ok) {
              throw new Error(isPl ? 'Błąd sprawdzania statusu zadania.' : isUk ? 'Помилка перевірки статусу завдання.' : 'Status check failed.');
            }
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              const rawReport = statusData.report || statusData.result || '';
              const resText = typeof rawReport === 'string'
                ? rawReport
                : (typeof rawReport === 'object' && rawReport !== null
                    ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2))
                    : String(rawReport));

              if (!resText) {
                throw new Error(isPl ? 'Serwer zwrócił pusty raport.' : isUk ? 'Сервер повернув порожній результат звіту.' : 'Server returned empty report.');
              }
              setReportResult(resText);
              setIsProcessing(false);
            } else if (statusData.status === 'failed') {
              setErrorMessage(statusData.error || (isPl ? 'Błąd generowania raportu.' : isUk ? 'Помилка генерації звіту.' : 'Report generation failed.'));
              setIsProcessing(false);
            } else {
              setTimeout(pollJob, 2500);
            }
          } catch (pollErr: any) {
            console.error('Polling error:', pollErr);
            setErrorMessage(pollErr.message || (isPl ? 'Wystąpił błąd podczas pobierania wyników.' : isUk ? 'Сталася помилка при отриманні результатів.' : 'Polling error occurred.'));
            setIsProcessing(false);
          }
        };
        pollJob();
      } else if (data.report) {
        setReportResult(data.report);
        setIsProcessing(false);
      } else {
        throw new Error(isPl ? 'Nieoczekiwana odpowiedź serwera.' : isUk ? 'Неочікувана відповідь сервера.' : 'Unexpected server response.');
      }
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || (isPl ? 'Wystąpił błąd podczas analizy.' : isUk ? 'Сталася помилка під час аналізу.' : 'An error occurred.'));
      setIsProcessing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!reportResult) return;
    try {
      const name = (agentConfig.nameUk || "Construction_Audit_Report").replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_");
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: reportResult, fileName: name })
      });
      if (!response.ok) throw new Error(isPl ? 'Nie udało się pobrać Word pliku.' : isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message);
    }
  };

  // Switch demo scenarios
  const applyPresetScenario = (scenario: 'ua' | 'pl' | 'eu') => {
    if (scenario === 'ua') {
      setField1("Акт приймання виконаних будівельних робіт (форма № КБ-2в) та Довідка КБ-3 / Капітальний ремонт лікарні");
      setField2("Тверда договірна ціна (14 850 000 грн з ПДВ) / Період: липень 2026 р.");
      setField3("Укладання бетону B25: 420 м³ (за актом) проти 310 м³ (за проектом та журналом робіт); Монтаж металоконструкцій: 24.5 т (проти 18.2 т)");
      setField4("Бетон B25: 3 850 грн/м³ (ринкова 2 950 грн/м³ — завищення на 30%); Робота автокрана 25т: 120 маш-год (завищено на 40 маш-год)");
      setField5("ЗВВ розраховані за усередненими показниками ДБН (коефіцієнт 1.25), прибуток 4.8 грн/люд-год, єдиний внесок 22%");
      setField6("Акти на приховані роботи № 12–28, паспорти якості на арматуру А500С, протоколи випробування бетону на 28 добу, журнал авторського нагляду");
      setNotesText("Провести детальний комплаєнс-аудит наданого Акта приймання виконаних будівельних робіт форми № КБ-2в та Довідки про вартість виконаних робіт форми № КБ-3. Перевірити відповідність обсягів робіт робочому проєкту та журналу робіт (форма КБ-6). Зіставити ціни на будівельні матеріали з даними моніторингу Мінрегіону/ДерждорНДІ. Перевірити правильність нарахування загальновиробничих витрат, кошторисного прибутку та ПДВ. Сформувати дефектну відомість завищень та підсумковий Акт зауважень замовника у Word (.docx) за ДСТУ 4163:2020.");
    } else if (scenario === 'pl') {
      setField1("Protokół odbioru robót budowlano-montażowych nr 07/2026 / Budowa hali magazynowo-logistycznej w Pruszkowie");
      setField2("Wynagrodzenie ryczałtowe / Kosztorysowe (2 450 000 PLN netto) / Fakturowanie KSeF");
      setField3("Roboty żelbetowe fundamentów: 220 m³ (w protokole) vs 175 m³ (według projektu i Książki Obmiarów); Konstrukcje stalowe: 48 t");
      setField4("Beton towarowy C25/30: 420 PLN/m³ (baza SEKOCENBUD: 340 PLN/m³ — zawyżenie o 23%); Praca żurawia wieżowego: 240 PLN/h");
      setField5("Narzuty: Koszty pośrednie Kp = 68% (R+S), Zysk kalkulacyjny Z = 12% (R+S+Kp), Stawka robocizny R = 32,50 PLN/r-g");
      setField6("Wpisy do Dziennika Budowy nr 18/2026, Protokoły odbioru robót zanikających, Atesty hutnicze stali B500SP, Świadectwa ITB");
      setNotesText("Przeprowadzić kompleksowy audyt zgłoszonego protokołu odbioru robót budowlanych oraz kosztorysu powykonawczego zgodnie z Prawem budowlanym i normami KNR. Zweryfikować obmiary fundamentów i posadzek przemysłowych z dokumentacją projektową. Porównać stawki materiałowe i sprzętowe ze średnimi cenami kwartalnymi SEKOCENBUD. Obliczyć kwotę nienależnego zawyżenia i sformułować oficjalny protokół uwag inwestora do potrącenia z płatności końcowej.");
    } else if (scenario === 'eu') {
      setField1("FIDIC Red Book Subcontract Interim Payment Certificate (IPC No. 4) / Highway Bridge Reconstruction Project");
      setField2("Re-measured Bill of Quantities (BOQ Unit Rates) / Total Subcontract: €3,200,000 / FIDIC Conditions");
      setField3("Asphalt concrete base course: 4,800 tonnes claimed vs 3,950 tonnes verified by Engineer's site surveys; Rebar: 185 tonnes");
      setField4("Bitumen polymer mix: €145/tonne (market benchmark €118/tonne); Heavy road paver plant hire: 180 plant-hours claimed vs 110 GPS-logged hours");
      setField5("Site Overheads & Profit markups: 18.5% on direct labor and plant, Retention money deduction: 5% per Clause 14.3 FIDIC");
      setField6("Engineer's Site Inspection Sheets, Laboratory Core Density Test Certificates, Asphalt Temperature Control Logs, Subcontractor Daily Reports");
      setNotesText("Audit Interim Payment Certificate IPC #4 under FIDIC Red Book Subcontract. Cross-check certified quantities with independent GPS/drone survey measurements. Verify material escalation price formula adjustments and plant standby hours. Generate Engineer's Payment Recommendation and Discrepancy Statement in Word (.docx) with clear deductions summary.");
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-16 font-mono max-w-5xl mx-auto px-4">
      {/* Top Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            🏠 {isPl ? 'Strona Główna' : isUk ? 'На головну' : 'Home'}
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            <ArrowLeft className="w-4 h-4 text-sky-400" />
            <span>{isPl ? '← Powrót do Budownictwa' : isUk ? '← До Центру Будівництва' : '← Back to Construction Hub'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-sky-500/20 text-sky-300 border border-sky-400/30 rounded-md flex items-center gap-1">
            <Globe className="w-3.5 h-3.5 text-sky-400" />
            <span>UA • PL • EU JURISDICTIONS</span>
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
            ДСТУ 4163 • KNR • SEKOCENBUD • FIDIC
          </span>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">{isPl ? "Aktywny kontekst firmy: " : isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isPl ? "Baza wiedzy przedsiębiorstwa" : isUk ? "База знань компанії" : "Company Knowledge Base")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            {isPl ? "Corporate DNA" : isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Hero Banner */}
      <div className="bg-slate-950 border border-sky-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl space-y-4">
        <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
          <div className="p-4 bg-sky-950/80 border border-sky-500/40 rounded-2xl text-sky-400 shrink-0 shadow-lg">
            <AgentIcon name={agentConfig.icon} className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-sky-500/30 text-sky-200 border border-sky-400/40 rounded">
                {getLocalizedAgentCategory(agentConfig, language)}
              </span>
              <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-200 border border-emerald-400/40 rounded flex items-center gap-1">
                <ShieldCheck className="w-3 h-3 text-emerald-400" />
                <span>100% Нормативний Комплаєнс</span>
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-extrabold uppercase tracking-wide text-white">
              {getLocalizedAgentName(agentConfig, language)}
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
              {getLocalizedAgentDesc(agentConfig, language)}
            </p>
          </div>
        </div>
      </div>

      {/* Fast Preset Selectors Header */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-white space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-extrabold uppercase tracking-wide flex items-center gap-1.5 text-amber-400">
            <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
            <span>{isPl ? "⚡ Gotowe scenariusze audytu budowlanego (1 kliknięcie):" : isUk ? "⚡ Готові сценарії аудиту будівельних актів (1 клік):" : "⚡ Ready Construction Audit Scenarios (1-Click Fill):"}</span>
          </span>
          <span className="text-[10px] text-slate-400 font-mono">КБ-2в • КБ-3 • KNR • SEKOCENBUD • FIDIC</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => applyPresetScenario('ua')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-emerald-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-emerald-300 group-hover:text-emerald-200 flex items-center gap-1.5">
              <span>🇺🇦 Акти КБ-2в та КБ-3 (Україна)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">ДСТУ • ДБН А.3.1-5 • Бетон/Метал • ЗВВ</div>
          </button>

          <button
            type="button"
            onClick={() => applyPresetScenario('pl')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-blue-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-blue-300 group-hover:text-blue-200 flex items-center gap-1.5">
              <span>🇵🇱 Protokół Robót & Obmiar (Polska)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">Prawo budowlane • SEKOCENBUD • KNR • KSeF</div>
          </button>

          <button
            type="button"
            onClick={() => applyPresetScenario('eu')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-purple-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-purple-300 group-hover:text-purple-200 flex items-center gap-1.5">
              <span>🇪🇺 FIDIC IPC No. 4 (Europe / BOQ)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">FIDIC Red Book • BOQ Re-measurement • IPC</div>
          </button>
        </div>
      </div>

      {/* Main Parameters Form */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-6 text-white shadow-xl">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-sky-300 flex items-center gap-2 border-b border-slate-800 pb-3">
          <Building2 className="w-4 h-4 text-sky-400" />
          <span>{isPl ? 'PARAMETRY WEJŚCIOWE OBIEKTU I DOKUMENTACJI BUDOWLANEJ:' : isUk ? 'ВХІДНІ ПАРАМЕТРИ АКТІВ ТА БУДІВЕЛЬНОЇ ДОКУМЕНТАЦІЇ:' : 'CONSTRUCTION ACTS & BOQ PARAMETERS:'}</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">1. {getFieldLabel(defaultDemo, 1, language)}</label>
            <input
              type="text"
              value={field1}
              onChange={e => setField1(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">2. {getFieldLabel(defaultDemo, 2, language)}</label>
            <input
              type="text"
              value={field2}
              onChange={e => setField2(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">3. {getFieldLabel(defaultDemo, 3, language)}</label>
            <input
              type="text"
              value={field3}
              onChange={e => setField3(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">4. {getFieldLabel(defaultDemo, 4, language)}</label>
            <input
              type="text"
              value={field4}
              onChange={e => setField4(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">5. {getFieldLabel(defaultDemo, 5, language)}</label>
            <input
              type="text"
              value={field5}
              onChange={e => setField5(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">6. {getFieldLabel(defaultDemo, 6, language)}</label>
            <input
              type="text"
              value={field6}
              onChange={e => setField6(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono"
            />
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">
            {isPl ? '7. ZAKRES WERYFIKACJI I WYMAGANIA AUDYTU:' : isUk ? '7. ДЕТАЛЬНИЙ ЗМІСТ АКТА ДЛЯ ПЕРЕВІРКИ ТА ЗАВДАННЯ АУДИТУ:' : '7. ACT DETAILS & AUDIT REQUIREMENTS:'}
          </label>
          <textarea 
            rows={5} 
            value={notesText} 
            onChange={e => setNotesText(e.target.value)} 
            className="w-full p-3.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-sky-500 focus:outline-none font-mono leading-relaxed" 
          />
        </div>

        {/* File Attachments */}
        <div className="space-y-3 pt-4 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center gap-2">
              <Upload className="w-4 h-4 text-sky-400" />
              <span>{isPl ? 'ZAŁĄCZNIKI (PDF / DOCX / XLSX / KOSZTORYSY / OBMIARY):' : isUk ? 'СУПРОВІДНІ ФАЙЛИ КБ-2в, КОШТОРИСІВ ТА ВІДОМОСТЕЙ (PDF / DOCX / XLSX):' : 'SUPPORTING ACT FILES & BOQ SPREADSHEETS:'}</span>
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="px-4 py-2.5 bg-sky-950/80 hover:bg-sky-900 border border-sky-500/40 text-sky-200 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2">
              <Upload className="w-4 h-4 text-sky-400" />
              <span>{isReadingFile ? (isPl ? 'Odczytywanie...' : 'Зчитування...') : (isPl ? '➕ Dodaj pliki aktu' : '➕ Завантажити акти / кошториси')}</span>
              <input type="file" multiple accept=".pdf,.docx,.xlsx,.csv,.json,.txt" onChange={handleFileUpload} className="hidden" />
            </label>

            {attachedFiles.map((file, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-slate-200">
                <FileCheck className="w-3.5 h-3.5 text-sky-400" />
                <span className="font-mono text-[11px] max-w-[150px] truncate">{file.name}</span>
                <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-400 p-0.5">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {errorMessage && (
          <div className="p-3.5 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Action Button */}
        <div className="pt-4 border-t border-slate-800 flex justify-end">
          <button
            onClick={handleRunAnalysis}
            disabled={isProcessing || !notesText.trim()}
            className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-sky-600 via-indigo-600 to-sky-700 hover:from-sky-500 hover:to-indigo-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 hover:scale-[1.01]"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-white" />
                <span>{isPl ? LOADING_STEPS_PL[loadingStepIndex] : isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span>{isPl ? '🚀 ROZPOCZNIJ AUDYT AKTÓW I KOSZTORYSÓW' : isUk ? '🚀 ЗАПУСТИТИ АУДИТ АКТІВ КБ-2в & КОШТОРИСІВ' : '🚀 RUN CONSTRUCTION ACTS AUDIT'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Result Section */}
      {reportResult && (
        <div className="bg-slate-950 border border-sky-500/40 rounded-2xl p-6 sm:p-8 space-y-4 text-white shadow-2xl animate-fade-in">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-2">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-sky-300 flex items-center gap-2">
              <FileText className="w-4 h-4 text-sky-400" />
              <span>{isPl ? 'OFICJALNY RAPORT AUDYTU I PROTOKÓŁ ROZBIEŻNOŚCI:' : isUk ? 'ОФІЦІЙНИЙ АУДИТОРСЬКИЙ ВИСНОВОК & ВІДОМІСТЬ РОЗБІЖНОСТЕЙ:' : 'OFFICIAL CONSTRUCTION AUDIT FINDINGS:'}</span>
            </h3>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleDownloadWord}
                className="px-3 py-1.5 bg-sky-950 hover:bg-sky-900 border border-sky-500/40 text-sky-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <Download className="w-3.5 h-3.5 text-sky-400" />
                <span>Word (.docx)</span>
              </button>

              <ExcelExportButton
                reportText={reportResult}
                fileName={`${agentConfig.nameUk || 'Construction_Audit'}_data`}
                className="px-3 py-1.5 bg-emerald-950 hover:bg-emerald-900 border border-emerald-500/40 text-emerald-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              />

              <TelegramExportButton
                reportText={reportResult}
                agentName={getLocalizedAgentName(agentConfig, language)}
              />

              <button
                onClick={() => setShowSignModal(true)}
                className="px-3 py-1.5 bg-amber-950 hover:bg-amber-900 border border-amber-500/40 text-amber-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                <span>{isPl ? '✍️ Podpisz QES' : isUk ? '✍️ Підписати КЕП' : '✍️ Sign QES'}</span>
              </button>

              <button
                onClick={() => {
                  navigator.clipboard.writeText(reportResult);
                  setIsCopied(true);
                  setTimeout(() => setIsCopied(false), 2000);
                }}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-slate-300" />}
                <span>{isCopied ? (isPl ? 'Skopiowano!' : isUk ? 'Скопійовано!' : 'Copied!') : (isPl ? 'Kopiuj' : isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
            </div>
          </div>

          <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-5 text-xs font-mono leading-relaxed text-slate-200 whitespace-pre-wrap max-h-[650px] overflow-y-auto">
            {reportResult}
          </div>
        </div>
      )}

      {showSignModal && reportResult && (
        <DigitalSignatureModal
          isOpen={showSignModal}
          onClose={() => setShowSignModal(false)}
          documentText={reportResult}
          documentTitle={getLocalizedAgentName(agentConfig, language)}
          language={language}
        />
      )}
    </div>
  );
}
